// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import clamp from 'lodash/clamp';
import {selx} from 'clsx';

import {SvgIconEl} from 'common/components/svg-icon';

import {ButtonEl} from '../button';
import {ButtonSize, ButtonTheme} from '../button/constants';
import {TooltipEl} from '../tooltip';
import {SpinnerEl} from '../spinner';
import {InputLabelEl} from './label';
import type {InputComponent} from './index';
import {
  InputChangeEvent,
  InputClipboardEvent,
  InputFocusEvent,
  InputInputEvent,
  InputKeyboardEvent,
  InputPointerEvent,
  InputType,
  ValidationStatus,
} from './types';
import styles from './index.module.styl';

function RightIcon({cmp}: {cmp: InputComponent}) {
  if (cmp.params.hideRightIcon) {
    return null;
  }
  const icon = cmp.params.rightIcon ?? {valid: `circle-checkmark`, invalid: `warning`}[cmp.params.validationStatus];
  if (cmp.params.rightIcon) {
    return (
      <div sel={selx(styles.mpInputRightIcon, styles.customRightIcon)}>
        <SvgIconEl attrs={{icon}} />
        {cmp.params.helpText ? <TooltipEl attrs={{text: cmp.params.helpText}} /> : null}
      </div>
    );
  } else if (cmp.params.validationStatus) {
    return (
      <div
        sel={selx(styles.mpInputRightIcon, {
          [styles.valid]: cmp.params.validationStatus === `valid`,
          [styles.invalid]: cmp.params.validationStatus === `invalid`,
        })}
      >
        <SvgIconEl attrs={{icon}} />
      </div>
    );
  } else if (cmp.params.alwaysShowClear || (cmp.state.value && cmp.params.showClear)) {
    return (
      <div
        attrs={{elref: `clearButton`}}
        sel={selx(styles.mpInputRightIcon, styles.clickable)}
        on={{
          click: (ev) => cmp.handleClearInput(ev),
        }}
      >
        <SvgIconEl attrs={{icon: `circle-clear`}} sel={selx(styles.mpInputClickableIcon)} />
      </div>
    );
  } else if (cmp.params.preText && cmp.params.helpText) {
    return (
      <div sel={selx(styles.mpInputRightIcon)}>
        <SvgIconEl attrs={{icon: `info-xs`}} />
        <TooltipEl attrs={{text: cmp.params.helpText}} />
      </div>
    );
  }
  return null;
}

function NumericControls({cmp}: {cmp: InputComponent}) {
  return (
    <div sel={selx(styles.mpInputNumericArrowControls)}>
      <ButtonEl
        sel={selx(styles.numericArrowButton)}
        attrs={{
          theme: ButtonTheme.Tertiary,
          size: ButtonSize.Tiny,
          icon: `caret-up`,
        }}
        on={{
          mousedown: (ev) => {
            // using onmousedown and preventDefault() here instead of onclick to keep focus on the input
            ev.preventDefault();
            cmp.update({
              value: String(
                clamp(parseInt(cmp.state.value, 10) + 1, parseInt(cmp.params.min, 10), parseInt(cmp.params.max, 10)),
              ),
            });
            cmp.debouncedChange({
              ...ev,
              target: cmp.searchInput,
            });
          },
        }}
      />
      <ButtonEl
        sel={selx(styles.numericArrowButton)}
        attrs={{
          theme: ButtonTheme.Tertiary,
          size: ButtonSize.Tiny,
          icon: `caret-down`,
        }}
        on={{
          mousedown: (ev) => {
            // using onmousedown and preventDefault() here instead of onclick to keep focus on the input
            ev.preventDefault();
            cmp.update({
              value: String(
                clamp(parseInt(cmp.state.value, 10) - 1, parseInt(cmp.params.min, 10), parseInt(cmp.params.max, 10)),
              ),
            });
            cmp.debouncedChange({
              ...ev,
              target: cmp.searchInput,
            });
          },
        }}
      />
    </div>
  );
}

export function Template(this: InputComponent) {
  const {value} = this.state;
  const validationStatus = this.params.validationStatus;
  const isValid = validationStatus === ValidationStatus.Valid;
  const isInvalid = validationStatus === ValidationStatus.Invalid;

  return (
    <div
      sel={selx(styles.mpInputContainer, {
        [styles.themeWhite]: this.params.theme === `white`,
      })}
    >
      {this.params.label ? (
        <InputLabelEl
          sel={selx(styles.inputLabel)}
          attrs={{
            label: this.params.label,
            'field-id': this.params.fieldId,
            necessary: this.params.necessary,
            'help-text': this.params.helpText,
            'extra-info': this.params.extraInfo,
          }}
          on={{
            click: () => this.params.disabled === false && this.focus(),
          }}
        />
      ) : null}
      <div
        sel={selx(styles.mpInputFieldContainer, {
          [styles.hasMinInputWidth]: Boolean(this.params.minInputWidth),
          [styles.hasMultilinePrefix]: this.params.multilinePrefix,
          [styles.hideFocusStyling]: this.params.hideFocusStyling,
          [styles.isDisabled]: this.params.disabled,
          [styles.sizeSmall]: this.params.size === `small`,
          [styles.sizeMedium]: this.params.size === `medium`,
          [styles.sizeLarge]: this.params.size === `large`,
          [styles.typeNumber]: this.params.type === `number`,
          [styles.typePassword]: this.params.type === `password`,
          [styles.typeText]: this.params.type === `text`,
          [styles.isValid]: isValid,
        })}
        // For whatever reason we have to leave this as a class or
        // userEvent.type doesn't work breaking a bunch of Storybook tests...
        // Doesn't really matter since they'll all be lumped up again when
        // this is converted to React
        class={{
          [styles.isInvalid]: isInvalid,
        }}
        on={{
          click: () => this.focus(),
        }}
      >
        {(() => {
          const TagName = this.params.type === `textarea` ? `textarea` : `input`;

          return (
            <>
              <div sel={selx(styles.mpInputLeftArea)}>
                {this.params.icon && (
                  <div sel={selx(`mp-input-left-icon`)}>
                    {this.params.icon === `spinner` ? (
                      <SpinnerEl sel={selx(styles.mpInputLeftIcon)} />
                    ) : (
                      <SvgIconEl attrs={{icon: this.params.icon}} sel={selx(styles.mpInputLeftIcon)} />
                    )}
                  </div>
                )}
                {this.params.preText && (
                  <div
                    sel={selx(styles.mpInputPreText, {
                      [styles.sizeSmall]: this.params.size === `small`,
                      [styles.sizeMedium]: this.params.size === `medium`,
                      [styles.sizeLarge]: this.params.size === `large`,
                    })}
                    on={{click: () => this.focus()}}
                  >
                    {this.params.preText}
                  </div>
                )}
              </div>
              <div
                sel={selx(styles.mpInputWrapper)}
                hook={{insert: ({elm}) => this.handleInputWrapperInsert(elm as HTMLElement)}}
              >
                {this.params.prefixRenderer &&
                  (this.params.multilinePrefix ? (
                    this.params.prefixRenderer()
                  ) : (
                    <div hook={{insert: ({elm}) => this.handleInputPrefixInsert(elm as HTMLElement)}}>
                      {this.params.prefixRenderer()}
                    </div>
                  ))}
                <TagName
                  sel={selx(styles.mpInput)}
                  style={{
                    'text-align': this.params.textAlign,
                    ...(this.params.minInputWidth && {'min-width': `${this.params.minInputWidth}px`}),
                    ...(this.params.multilinePrefix && {
                      width: this.state.textWidth ? `${this.state.textWidth}px` : undefined,
                    }),
                  }}
                  attrs={{
                    ...(this.params.ariaLabel ? {[`aria-label`]: this.params.ariaLabel} : {}),
                    autocomplete: this.params.autocomplete,
                    elref: `inputTag`,
                    id: this.params.fieldId,
                    name: this.params.fieldName,
                    placeholder: this.params.placeholder,
                    disabled: this.params.disabled,
                    readonly: this.params.readonly,
                    size: 1,
                    // We do custom comma formatting on number type inputs.
                    // Browsers won't accept something like '1,000' as a valid
                    // do do not use the "type" attribute in this case.
                    type: TagName === `input` && this.params.type !== InputType.Number ? this.params.type : null,
                    maxlength: this.params.maxlength,
                    minlength: TagName === `input` ? this.params.min : null,
                    min: this.supportsMinMaxAttrs() ? this.params.min : null, // only enforces arrow key decrements
                    max: this.supportsMinMaxAttrs() ? this.params.max : null, // only enforces arrow key increments
                    step: this.supportsMinMaxAttrs() ? this.params.step : null, // only enforces arrow key increments
                    'not-focusable': this.params.notFocusable,
                  }}
                  props={{
                    value: this.displayValue,
                  }}
                  on={{
                    click: (ev: InputPointerEvent) => this.handleClick(ev),
                    input: (ev: InputInputEvent) => this.handleInput(ev),
                    blur: (ev: InputFocusEvent) => this.handleBlur(ev),
                    focus: (ev: InputFocusEvent) => this.handleFocus(ev),
                    change: (ev: InputChangeEvent) => this.handleChange(ev),
                    keydown: (ev: InputKeyboardEvent) => this.handleKeydown(ev),
                    keyup: (ev: InputKeyboardEvent) => this.handleKeyup(ev),
                    paste: (ev: InputClipboardEvent) => this.handlePaste(ev),
                  }}
                  hook={{
                    insert: ({elm}) => {
                      this.handleSearchInputInsert(elm as HTMLInputElement);
                    },
                  }}
                />
              </div>
              <div sel={selx(styles.mpInputRightArea)}>
                {this.params.suffixRenderer?.()}
                {this.params.actionText && (this.params.alwaysShowActionText || value) ? (
                  <div sel={selx(styles.mpInputActionText)}>{this.params.actionText}</div>
                ) : null}
                {this.params.type === InputType.Number && this.params.showNumericInputControls && (
                  <NumericControls cmp={this} />
                )}
                <RightIcon cmp={this} />
              </div>
            </>
          );
        })()}
      </div>
      {validationStatus === `invalid` && this.params.errorMessage ? (
        <div attrs={{elref: `inputErrorMessage`}} sel={selx(styles.mpInputErrorMessage)}>
          {this.params.errorMessage}
        </div>
      ) : null}
    </div>
  );
}
